import React from 'react';
import {
  getRecommendations,
  getRecommendationsFromML,
  getRelatedAppsOrRecommendationsFromML,
  getTagsDataBySlugs,
} from '../../../../api/api';
import { SolutionsGrid } from './solutions-grid';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { withExperiments } from '../../../../component-decorators/with-experiments';
import type { MarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import { withMarketplaceStore } from '../../../../component-decorators/with-marketplace-store';
import type {
  WebSolution,
  WebSolutionBase,
} from '@wix/ambassador-marketplace/types';
import { biLogger } from '../../../../bi';
import { v4 as generateUuidV4 } from 'uuid';
import { appMarketAppCollectionImpressions } from '@wix/bi-logger-market/v2';

export interface ISolutionsGridContainerProps
  extends Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
  solutions?: WebSolutionBase[];
  appId: string;
  locale?: string;
  countryCode?: string;
  getAppInstance?(appDefID: string): string;
}

export const dtoToGridsSolutions = (
  solutionDto: WebSolution,
): ISolutionsGridContainerProps => {
  const webSolutionsBase =
    solutionDto.company && solutionDto.company.webSolutions
      ? solutionDto.company.webSolutions.map(companySolutionsToBaseInfo)
      : undefined;
  return {
    appId: solutionDto.baseInfo.id,
    solutions: webSolutionsBase,
  };
};

@withMarketplaceStore
@withExperiments
export class SolutionsGridContainer extends React.Component<
  ISolutionsGridContainerProps,
  any
> {
  constructor(props) {
    super(props);
    this.state = {
      isFetching: true,
    };
  }

  render() {
    return (
      <div>
        {this.state.isFetching ? null : (
          <SolutionsGrid
            solutions={this.state.solutions}
            referralSectionName={this.state.referralSectionName}
            collimp_id={this.state.collimp_id}
            referralInfo="may_also_like"
          />
        )}
      </div>
    );
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.appId !== this.props.appId) {
      await this.initialize(this.props.locale, this.props.countryCode);
    }
  }

  async componentDidMount() {
    await this.initialize(this.props.locale, this.props.countryCode);
  }

  private async initialize(locale: string, countryCode: string) {
    let solutions, webSolutionsBase: WebSolutionBase[];
    let webSolutions, referralSectionName;
    const fetchRecommendationsFromML = this.props?.experiments?.enabled(
      'specs.marketplace.useMLInManageAndAppPage',
    );
    const shouldFetchRelatedApps = this.props?.experiments?.enabled(
      'specs.marketplace.fetchRelatedAppsInAppPage',
    );
    if (this.props.marketplaceStore.metaSiteId) {
      const signedInstance = this.props?.getAppInstance?.(
        '22bef345-3c5b-4c18-b782-74d4085112ff',
      );
      webSolutions = fetchRecommendationsFromML
        ? await getRelatedAppsOrRecommendationsFromML(
            this.props?.appId,
            '10',
            locale,
            signedInstance,
            shouldFetchRelatedApps,
          )
        : await getRecommendations(signedInstance, locale);
      webSolutionsBase = fetchRecommendationsFromML
        ? shouldFetchRelatedApps
          ? webSolutions.webSolutions
          : webSolutions.webSolutions?.map((sol) => sol.baseInfo)
        : webSolutions.map((sol) => sol.baseInfo);
      solutions = webSolutionsBase
        .filter((solution) => solution.id !== this.props.appId)
        .slice(0, 4);
      referralSectionName = fetchRecommendationsFromML
        ? 'may_also_like_ML'
        : 'may_also_like';
    } else {
      const { webSolutions } = await getTagsDataBySlugs(
        ['staff-picks'],
        locale,
        countryCode,
        true,
        true,
      );
      webSolutionsBase = webSolutions.map((sol) => sol.baseInfo);
      solutions = webSolutionsBase
        .filter((solution) => solution.id !== this.props.appId)
        .slice(0, 4);
    }

    const appIds = solutions.map((webSolution) => webSolution.id);
    const appIdsList = appIds?.join(',');
    const collimp_id = generateUuidV4();

    biLogger
      .report(
        appMarketAppCollectionImpressions({
          apps_list: appIdsList,
          collimp_id,
          number_of_apps: 4,
          tag_name: 'user-recommendations',
          tag_type: 'app_page',
          prediction_type: fetchRecommendationsFromML
            ? webSolutions?.predictionType
            : 'old_rec',
        }),
      )
      .catch((e) => console.log(e));

    this.setState({
      isFetching: false,
      solutions,
      collimp_id,
      referralSectionName,
    });
  }
}
export const companySolutionsToBaseInfo = (solution): WebSolutionBase => {
  const {
    id,
    name,
    teaser,
    icon,
    type,
    description,
    slug,
    pricing,
    company,
    reviewStars,
    numberOfReviews,
    Tags,
    openConsentIn,
  } = solution;
  return {
    id,
    name,
    teaser,
    icon,
    type,
    description,
    slug,
    pricing,
    company,
    reviewStars,
    numberOfReviews,
    Tags,
    openConsentIn,
  };
};
